import { useState, useEffect } from "react";
import { 
  ShoppingCart, Barcode, Package, Receipt, TrendingUp,
  DollarSign, AlertCircle, Plus, Search, Edit2, Trash2,
  ArrowUpRight, ArrowDownRight, Clock, Calendar, Boxes,
  Tags, LayoutDashboard, CheckCircle, XCircle, Zap,
  RefreshCw, Printer, CreditCard, Banknote, Timer
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

// HUBER MART - Complete Grocery Store Management System
const GroceryStoreDemo = ({ isDark }) => {
  const [activeTab, setActiveTab] = useState('register');
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [scanInput, setScanInput] = useState('');
  const [cart, setCart] = useState([]);
  const [lastScanned, setLastScanned] = useState(null);
  const [scanAnimation, setScanAnimation] = useState(false);
  
  // Business info
  const businessInfo = {
    name: "Huber Mart",
    tagline: "Family Grocery & Homemade Foods",
    location: "Columbus, OH"
  };
  
  // Product catalog with barcodes, expiration tracking
  const [products] = useState([
    { barcode: "4011", name: "Bananas", category: "Produce", cost: 0.45, price: 0.69, stock: 150, unit: "lb", reorderAt: 50, expiry: null, taxable: false },
    { barcode: "4065", name: "Green Peppers", category: "Produce", cost: 0.85, price: 1.29, stock: 45, unit: "lb", reorderAt: 20, expiry: null, taxable: false },
    { barcode: "070847000280", name: "Whole Milk 1 Gal", category: "Dairy", cost: 2.89, price: 4.29, stock: 48, unit: "ea", reorderAt: 15, expiry: "Dec 28", taxable: false },
    { barcode: "070847000297", name: "2% Milk 1 Gal", category: "Dairy", cost: 2.79, price: 3.99, stock: 36, unit: "ea", reorderAt: 15, expiry: "Dec 27", taxable: false },
    { barcode: "036800169913", name: "Greek Yogurt", category: "Dairy", cost: 0.95, price: 1.79, stock: 8, unit: "ea", reorderAt: 20, expiry: "Dec 22", taxable: false },
    { barcode: "072400123456", name: "White Bread Loaf", category: "Bakery", cost: 1.45, price: 2.49, stock: 24, unit: "ea", reorderAt: 10, expiry: "Dec 20", taxable: false },
    { barcode: "072400123463", name: "Whole Wheat Bread", category: "Bakery", cost: 1.85, price: 3.49, stock: 18, unit: "ea", reorderAt: 10, expiry: "Dec 21", taxable: false },
    { barcode: "041130000089", name: "Chicken Breast", category: "Meat", cost: 3.20, price: 5.99, stock: 65, unit: "lb", reorderAt: 25, expiry: "Dec 19", taxable: false },
    { barcode: "041130000096", name: "Ground Beef 80/20", category: "Meat", cost: 4.50, price: 6.99, stock: 40, unit: "lb", reorderAt: 20, expiry: "Dec 18", taxable: false },
    { barcode: "049000000443", name: "Coca-Cola 12pk", category: "Beverages", cost: 4.50, price: 6.99, stock: 120, unit: "ea", reorderAt: 30, expiry: null, taxable: true },
    { barcode: "012000001536", name: "Pepsi 12pk", category: "Beverages", cost: 4.25, price: 6.49, stock: 85, unit: "ea", reorderAt: 30, expiry: null, taxable: true },
    { barcode: "028400000345", name: "Doritos Nacho", category: "Snacks", cost: 2.80, price: 4.99, stock: 55, unit: "ea", reorderAt: 20, expiry: null, taxable: true },
    { barcode: "028400000352", name: "Lay's Classic", category: "Snacks", cost: 2.50, price: 4.49, stock: 62, unit: "ea", reorderAt: 20, expiry: null, taxable: true },
    { barcode: "075450000123", name: "Basmati Rice 5lb", category: "Grocery", cost: 5.25, price: 8.99, stock: 45, unit: "ea", reorderAt: 15, expiry: null, taxable: false },
    { barcode: "075450000130", name: "Jasmine Rice 5lb", category: "Grocery", cost: 4.95, price: 7.99, stock: 38, unit: "ea", reorderAt: 15, expiry: null, taxable: false },
    { barcode: "HM-SAM-001", name: "Homemade Samosas (6pc)", category: "Homemade", cost: 2.50, price: 5.99, stock: 24, unit: "ea", reorderAt: 8, expiry: "Today", taxable: true },
    { barcode: "HM-ROT-001", name: "Fresh Roti (10pc)", category: "Homemade", cost: 1.80, price: 4.49, stock: 18, unit: "ea", reorderAt: 6, expiry: "Today", taxable: true },
    { barcode: "HM-PAK-001", name: "Chicken Pakora (8pc)", category: "Homemade", cost: 3.00, price: 6.99, stock: 12, unit: "ea", reorderAt: 5, expiry: "Today", taxable: true },
    { barcode: "071856000012", name: "Frozen Pizza Pepperoni", category: "Frozen", cost: 3.75, price: 6.49, stock: 28, unit: "ea", reorderAt: 10, expiry: null, taxable: false },
    { barcode: "071856000029", name: "Ice Cream Vanilla", category: "Frozen", cost: 3.25, price: 5.49, stock: 20, unit: "ea", reorderAt: 8, expiry: null, taxable: false },
  ]);
  
  // Live scan feed - simulates what's being scanned at register
  const [liveScanFeed, setLiveScanFeed] = useState([
    { time: "2 sec ago", barcode: "4011", product: "Bananas", qty: "2.3 lb", price: 1.59, register: 1 },
    { time: "15 sec ago", barcode: "070847000280", product: "Whole Milk 1 Gal", qty: "1", price: 4.29, register: 1 },
    { time: "28 sec ago", barcode: "HM-SAM-001", product: "Homemade Samosas (6pc)", qty: "2", price: 11.98, register: 1 },
    { time: "45 sec ago", barcode: "049000000443", product: "Coca-Cola 12pk", qty: "1", price: 6.99, register: 2 },
    { time: "1 min ago", barcode: "028400000345", product: "Doritos Nacho", qty: "1", price: 4.99, register: 2 },
  ]);
  
  // Today's receipts/transactions
  const receipts = [
    { id: "RCP-4521", time: "2:45 PM", items: 8, subtotal: 47.85, tax: 2.15, total: 50.00, payment: "Card", register: 1 },
    { id: "RCP-4520", time: "2:32 PM", items: 12, subtotal: 89.50, tax: 4.28, total: 93.78, payment: "Cash", register: 1 },
    { id: "RCP-4519", time: "2:15 PM", items: 3, subtotal: 18.97, tax: 0, total: 18.97, payment: "Card", register: 2 },
    { id: "RCP-4518", time: "1:58 PM", items: 15, subtotal: 142.30, tax: 8.45, total: 150.75, payment: "Card", register: 1 },
    { id: "RCP-4517", time: "1:42 PM", items: 5, subtotal: 32.45, tax: 1.12, total: 33.57, payment: "Cash", register: 2 },
    { id: "RCP-4516", time: "1:28 PM", items: 22, subtotal: 178.90, tax: 12.30, total: 191.20, payment: "Card", register: 1 },
  ];
  
  // Supplier orders
  const supplierOrders = [
    { id: "PO-892", supplier: "Sysco Foods", items: 45, total: 1245.00, date: "Today", status: "delivered", expected: "Dec 17" },
    { id: "PO-891", supplier: "Local Produce Co-op", items: 12, total: 380.50, date: "Today", status: "delivered", expected: "Dec 17" },
    { id: "PO-890", supplier: "Midwest Dairy", items: 24, total: 520.00, date: "Tomorrow", status: "in_transit", expected: "Dec 18" },
    { id: "PO-889", supplier: "Amul India Foods", items: 30, total: 875.00, date: "Dec 19", status: "ordered", expected: "Dec 19" },
    { id: "PO-888", supplier: "Fresh Bake Supply", items: 18, total: 290.00, date: "Dec 20", status: "ordered", expected: "Dec 20" },
  ];
  
  // Stats by period
  const periodStats = {
    today: { sales: 2847.50, cost: 1654.20, profit: 1193.30, transactions: 47, items: 412 },
    week: { sales: 18450.00, cost: 10820.00, profit: 7630.00, transactions: 312, items: 2847 },
    month: { sales: 78500.00, cost: 45200.00, profit: 33300.00, transactions: 1289, items: 11520 }
  };
  
  const stats = periodStats[selectedPeriod];
  
  // Calculate low stock items
  const lowStockItems = products.filter(p => p.stock <= p.reorderAt);
  
  // Calculate expiring soon items
  const expiringSoon = products.filter(p => p.expiry && (p.expiry === "Today" || p.expiry === "Dec 18" || p.expiry === "Dec 19"));
  
  // Simulate barcode scan
  const handleScan = (barcode) => {
    const product = products.find(p => p.barcode === barcode);
    if (product) {
      setScanAnimation(true);
      setLastScanned(product);
      
      // Add to cart
      setCart(prev => {
        const existing = prev.find(item => item.barcode === barcode);
        if (existing) {
          return prev.map(item => 
            item.barcode === barcode 
              ? { ...item, qty: item.qty + 1, total: (item.qty + 1) * item.price }
              : item
          );
        }
        return [...prev, { ...product, qty: 1, total: product.price }];
      });
      
      // Add to live feed
      setLiveScanFeed(prev => [{
        time: "Just now",
        barcode: product.barcode,
        product: product.name,
        qty: "1",
        price: product.price,
        register: 1
      }, ...prev.slice(0, 4)]);
      
      setTimeout(() => setScanAnimation(false), 500);
    }
    setScanInput('');
  };
  
  // Calculate cart totals
  const cartSubtotal = cart.reduce((sum, item) => sum + item.total, 0);
  const cartTax = cart.reduce((sum, item) => item.taxable ? sum + (item.total * 0.075) : sum, 0);
  const cartTotal = cartSubtotal + cartTax;
  
  // Clear cart
  const clearCart = () => {
    setCart([]);
    setLastScanned(null);
  };

  return (
    <div className="space-y-4">
      {/* Store Header */}
      <div className={`p-4 rounded-xl ${isDark ? 'bg-gradient-to-r from-emerald-900/50 to-slate-800 border border-emerald-700/50' : 'bg-gradient-to-r from-emerald-50 to-white border border-emerald-200'}`}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${isDark ? 'bg-emerald-600' : 'bg-emerald-500'} shadow-lg`}>
              <ShoppingCart className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>{businessInfo.name}</h2>
              <p className={`text-sm ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{businessInfo.tagline}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Live indicator */}
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${isDark ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}>
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span className={`text-xs font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>2 Registers Active</span>
            </div>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className={`px-3 py-2 rounded-lg text-sm font-medium ${isDark ? 'bg-slate-700 text-white border-slate-600' : 'bg-white text-slate-800 border-slate-300'} border`}
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className={`flex gap-1 p-1 rounded-xl overflow-x-auto ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
        {[
          { id: 'register', label: 'Register / POS', icon: Barcode },
          { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
          { id: 'products', label: 'Products', icon: Boxes },
          { id: 'receipts', label: 'Receipts', icon: Receipt },
          { id: 'orders', label: 'Supplier Orders', icon: Tags },
          { id: 'reports', label: 'Reports', icon: TrendingUp }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? isDark ? 'bg-emerald-600 text-white shadow-lg' : 'bg-white text-emerald-700 shadow-md'
                : isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* REGISTER / POS TAB - Primary Feature */}
      {activeTab === 'register' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left - Scanner & Product Lookup */}
          <div className="lg:col-span-2 space-y-4">
            {/* Barcode Scanner Input */}
            <Card className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'} ${scanAnimation ? 'ring-2 ring-emerald-500' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-xl ${isDark ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}>
                    <Barcode className={`w-8 h-8 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                  </div>
                  <div className="flex-1">
                    <input
                      type="text"
                      value={scanInput}
                      onChange={(e) => setScanInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleScan(scanInput)}
                      placeholder="Scan barcode or enter product code..."
                      className={`w-full px-4 py-3 rounded-lg border text-lg font-mono ${
                        isDark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-500' : 'bg-slate-50 border-slate-300 text-slate-800'
                      }`}
                      autoFocus
                    />
                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                      Try: 4011 (Bananas), HM-SAM-001 (Samosas), 049000000443 (Coca-Cola)
                    </p>
                  </div>
                  <button 
                    onClick={() => handleScan(scanInput)}
                    className={`px-4 py-3 rounded-lg font-medium ${isDark ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-emerald-500 text-white hover:bg-emerald-600'}`}
                  >
                    <Zap className="w-5 h-5" />
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Last Scanned Item */}
            {lastScanned && (
              <Card className={`${isDark ? 'bg-emerald-900/30 border-emerald-700/50' : 'bg-emerald-50 border-emerald-200'} animate-pulse-once`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <CheckCircle className={`w-8 h-8 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                      <div>
                        <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>{lastScanned.name}</p>
                        <p className={`text-sm ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                          {lastScanned.category} • Barcode: {lastScanned.barcode}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        ${lastScanned.price.toFixed(2)}
                      </p>
                      <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        {lastScanned.taxable ? '+ tax' : 'No tax (grocery)'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Live Scan Feed */}
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardHeader className="pb-2">
                <CardTitle className={`text-lg flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  <RefreshCw className={`w-4 h-4 ${isDark ? 'text-emerald-400' : 'text-emerald-600'} animate-spin`} />
                  Live Scan Feed
                  <span className={`text-xs font-normal ml-2 px-2 py-0.5 rounded-full ${isDark ? 'bg-slate-700 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>
                    Real-time
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-slate-700/30">
                  {liveScanFeed.map((scan, idx) => (
                    <div key={idx} className={`px-4 py-3 flex items-center justify-between ${idx === 0 && scanAnimation ? 'bg-emerald-900/20' : ''} ${isDark ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50'}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${
                          scan.register === 1 
                            ? isDark ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-100 text-blue-700'
                            : isDark ? 'bg-purple-900/50 text-purple-400' : 'bg-purple-100 text-purple-700'
                        }`}>
                          R{scan.register}
                        </div>
                        <div>
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{scan.product}</p>
                          <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                            {scan.barcode} • Qty: {scan.qty}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>${scan.price.toFixed(2)}</p>
                        <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{scan.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right - Current Cart / Receipt */}
          <div className="space-y-4">
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardHeader className={`${isDark ? 'bg-emerald-900/30' : 'bg-emerald-50'} rounded-t-xl`}>
                <div className="flex items-center justify-between">
                  <CardTitle className={`text-lg ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
                    Current Sale
                  </CardTitle>
                  <span className={`text-sm px-2 py-1 rounded ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-white text-slate-600'}`}>
                    Register 1
                  </span>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {cart.length === 0 ? (
                  <div className={`p-8 text-center ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    <Barcode className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Scan items to begin</p>
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto divide-y divide-slate-700/30">
                    {cart.map((item, idx) => (
                      <div key={idx} className={`px-4 py-2 flex items-center justify-between ${isDark ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50'}`}>
                        <div className="flex-1">
                          <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{item.name}</p>
                          <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                            ${item.price.toFixed(2)} x {item.qty}
                            {item.taxable && <span className="ml-1 text-amber-500">+tax</span>}
                          </p>
                        </div>
                        <p className={`font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>${item.total.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Totals */}
                <div className={`p-4 border-t ${isDark ? 'border-slate-700 bg-slate-700/30' : 'border-slate-200 bg-slate-50'}`}>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Subtotal</span>
                      <span className={isDark ? 'text-white' : 'text-slate-800'}>${cartSubtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Tax (7.5% on taxable)</span>
                      <span className={isDark ? 'text-white' : 'text-slate-800'}>${cartTax.toFixed(2)}</span>
                    </div>
                    <div className={`flex justify-between text-xl font-bold pt-2 border-t ${isDark ? 'border-slate-600' : 'border-slate-300'}`}>
                      <span className={isDark ? 'text-white' : 'text-slate-800'}>Total</span>
                      <span className={isDark ? 'text-emerald-400' : 'text-emerald-600'}>${cartTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Payment Buttons */}
                <div className="p-4 grid grid-cols-2 gap-2">
                  <button className={`flex items-center justify-center gap-2 py-3 rounded-lg font-medium ${isDark ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-500 text-white hover:bg-blue-600'}`}>
                    <CreditCard className="w-4 h-4" /> Card
                  </button>
                  <button className={`flex items-center justify-center gap-2 py-3 rounded-lg font-medium ${isDark ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-emerald-500 text-white hover:bg-emerald-600'}`}>
                    <Banknote className="w-4 h-4" /> Cash
                  </button>
                  <button 
                    onClick={clearCart}
                    className={`col-span-2 flex items-center justify-center gap-2 py-2 rounded-lg font-medium ${isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}
                  >
                    <XCircle className="w-4 h-4" /> Clear Sale
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-3">
              <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
                <CardContent className="p-3">
                  <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Today's Sales</p>
                  <p className={`text-lg font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>$2,847</p>
                </CardContent>
              </Card>
              <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
                <CardContent className="p-3">
                  <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Transactions</p>
                  <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>47</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* DASHBOARD TAB */}
      {activeTab === 'dashboard' && (
        <div className="space-y-4">
          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardContent className="p-4">
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Total Sales</p>
                <p className={`text-xl font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>${stats.sales.toLocaleString()}</p>
                <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{stats.transactions} transactions</p>
              </CardContent>
            </Card>
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardContent className="p-4">
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Cost of Goods</p>
                <p className={`text-xl font-bold ${isDark ? 'text-red-400' : 'text-red-600'}`}>${stats.cost.toLocaleString()}</p>
                <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Product costs</p>
              </CardContent>
            </Card>
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardContent className="p-4">
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Gross Profit</p>
                <p className={`text-xl font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>${stats.profit.toLocaleString()}</p>
                <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{((stats.profit / stats.sales) * 100).toFixed(1)}% margin</p>
              </CardContent>
            </Card>
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardContent className="p-4">
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Items Sold</p>
                <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>{stats.items.toLocaleString()}</p>
                <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Products scanned</p>
              </CardContent>
            </Card>
            <Card className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'} ${lowStockItems.length > 0 ? 'border-amber-500/50' : ''}`}>
              <CardContent className="p-4">
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Low Stock</p>
                <p className={`text-xl font-bold ${lowStockItems.length > 0 ? 'text-amber-500' : isDark ? 'text-white' : 'text-slate-800'}`}>{lowStockItems.length}</p>
                <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Need reorder</p>
              </CardContent>
            </Card>
            <Card className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'} ${expiringSoon.length > 0 ? 'border-red-500/50' : ''}`}>
              <CardContent className="p-4">
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Expiring Soon</p>
                <p className={`text-xl font-bold ${expiringSoon.length > 0 ? 'text-red-500' : isDark ? 'text-white' : 'text-slate-800'}`}>{expiringSoon.length}</p>
                <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Check dates</p>
              </CardContent>
            </Card>
          </div>

          {/* Alerts Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Low Stock Alert */}
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardHeader className="pb-2">
                <CardTitle className={`text-base flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  Low Stock Alert
                  <span className={`text-xs ml-auto px-2 py-0.5 rounded-full ${isDark ? 'bg-amber-900/50 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>
                    {lowStockItems.length} items
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-48 overflow-y-auto">
                {lowStockItems.map((item, idx) => (
                  <div key={idx} className={`p-2 rounded-lg flex items-center justify-between ${isDark ? 'bg-amber-900/20' : 'bg-amber-50'}`}>
                    <div>
                      <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{item.name}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{item.category}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-amber-500">{item.stock} {item.unit}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Reorder: {item.reorderAt}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Expiring Soon */}
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardHeader className="pb-2">
                <CardTitle className={`text-base flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  <Timer className="w-5 h-5 text-red-500" />
                  Expiring Soon
                  <span className={`text-xs ml-auto px-2 py-0.5 rounded-full ${isDark ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-700'}`}>
                    {expiringSoon.length} items
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-48 overflow-y-auto">
                {expiringSoon.map((item, idx) => (
                  <div key={idx} className={`p-2 rounded-lg flex items-center justify-between ${
                    item.expiry === 'Today' 
                      ? isDark ? 'bg-red-900/30' : 'bg-red-50' 
                      : isDark ? 'bg-amber-900/20' : 'bg-amber-50'
                  }`}>
                    <div>
                      <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{item.name}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{item.category} • {item.stock} {item.unit}</p>
                    </div>
                    <div className={`text-sm font-bold ${item.expiry === 'Today' ? 'text-red-500' : 'text-amber-500'}`}>
                      {item.expiry}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Top Sellers & Category Performance */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardHeader className="pb-2">
                <CardTitle className={`text-base ${isDark ? 'text-white' : 'text-slate-800'}`}>Top Sellers Today</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {[
                  { name: "Bananas", sold: 89, revenue: 61.41 },
                  { name: "Whole Milk 1 Gal", sold: 34, revenue: 145.86 },
                  { name: "Homemade Samosas", sold: 28, revenue: 167.72 },
                  { name: "Coca-Cola 12pk", sold: 24, revenue: 167.76 },
                ].map((item, idx) => (
                  <div key={idx} className={`flex items-center justify-between p-2 rounded-lg ${isDark ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isDark ? 'bg-emerald-900 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>{idx + 1}</span>
                      <span className={isDark ? 'text-white' : 'text-slate-800'}>{item.name}</span>
                    </div>
                    <div className="text-right">
                      <span className={`font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>${item.revenue.toFixed(2)}</span>
                      <span className={`text-xs ml-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>({item.sold})</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardHeader className="pb-2">
                <CardTitle className={`text-base ${isDark ? 'text-white' : 'text-slate-800'}`}>Category Sales</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {[
                  { cat: "Produce", revenue: 412, pct: 28 },
                  { cat: "Homemade Foods", revenue: 385, pct: 24 },
                  { cat: "Dairy", revenue: 298, pct: 18 },
                  { cat: "Beverages", revenue: 245, pct: 15 },
                  { cat: "Snacks", revenue: 189, pct: 12 },
                ].map((item, idx) => (
                  <div key={idx} className={`p-2 rounded-lg ${isDark ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
                    <div className="flex justify-between mb-1">
                      <span className={isDark ? 'text-white' : 'text-slate-800'}>{item.cat}</span>
                      <span className={`font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>${item.revenue}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`flex-1 h-2 rounded-full ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
                        <div className="h-2 rounded-full bg-emerald-500" style={{ width: `${item.pct}%` }}></div>
                      </div>
                      <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{item.pct}%</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* PRODUCTS TAB */}
      {activeTab === 'products' && (
        <div className="space-y-4">
          <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg flex-1 ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
                  <Search className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
                  <input 
                    placeholder="Search by name or barcode..." 
                    className={`bg-transparent text-sm outline-none flex-1 ${isDark ? 'text-white placeholder-slate-500' : 'text-slate-800 placeholder-slate-400'}`}
                  />
                </div>
                <div className="flex gap-2">
                  <select className={`px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-slate-700 text-white border-slate-600' : 'bg-white text-slate-800 border-slate-300'} border`}>
                    <option>All Categories</option>
                    <option>Produce</option>
                    <option>Dairy</option>
                    <option>Meat</option>
                    <option>Bakery</option>
                    <option>Homemade</option>
                    <option>Beverages</option>
                    <option>Snacks</option>
                    <option>Grocery</option>
                    <option>Frozen</option>
                  </select>
                  <button className={`px-4 py-2 rounded-lg font-medium text-sm ${isDark ? 'bg-emerald-600 text-white' : 'bg-emerald-500 text-white'}`}>
                    <Plus className="w-4 h-4 inline mr-1" /> Add Product
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={isDark ? 'border-b border-slate-700 bg-slate-700/30' : 'border-b border-slate-200 bg-slate-50'}>
                      <th className={`text-left p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Barcode</th>
                      <th className={`text-left p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Product</th>
                      <th className={`text-left p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Category</th>
                      <th className={`text-right p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Cost</th>
                      <th className={`text-right p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Price</th>
                      <th className={`text-right p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Margin</th>
                      <th className={`text-right p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Stock</th>
                      <th className={`text-center p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Expiry</th>
                      <th className={`text-center p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Tax</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.slice(0, 12).map((item, idx) => {
                      const margin = Math.round((1 - item.cost / item.price) * 100);
                      const isLowStock = item.stock <= item.reorderAt;
                      const isExpiring = item.expiry === 'Today' || item.expiry === 'Dec 18';
                      return (
                        <tr key={idx} className={`${isDark ? 'border-b border-slate-700/50 hover:bg-slate-700/30' : 'border-b border-slate-100 hover:bg-slate-50'} transition-colors`}>
                          <td className={`p-3 font-mono text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{item.barcode}</td>
                          <td className={`p-3 font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
                            <div className="flex items-center gap-2">
                              {item.name}
                              {isLowStock && <AlertCircle className="w-3 h-3 text-amber-500" />}
                            </div>
                          </td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              item.category === 'Homemade' 
                                ? isDark ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'
                                : isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'
                            }`}>
                              {item.category}
                            </span>
                          </td>
                          <td className={`p-3 text-right ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>${item.cost.toFixed(2)}</td>
                          <td className={`p-3 text-right font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>${item.price.toFixed(2)}</td>
                          <td className="p-3 text-right">
                            <span className={`font-bold ${margin >= 40 ? 'text-emerald-500' : margin >= 25 ? 'text-amber-500' : 'text-red-500'}`}>
                              {margin}%
                            </span>
                          </td>
                          <td className={`p-3 text-right font-medium ${isLowStock ? 'text-amber-500' : isDark ? 'text-white' : 'text-slate-800'}`}>
                            {item.stock} {item.unit}
                          </td>
                          <td className="p-3 text-center">
                            {item.expiry ? (
                              <span className={`text-xs px-2 py-1 rounded ${
                                isExpiring 
                                  ? 'bg-red-100 text-red-700' 
                                  : isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'
                              }`}>
                                {item.expiry}
                              </span>
                            ) : (
                              <span className={`text-xs ${isDark ? 'text-slate-600' : 'text-slate-300'}`}>—</span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            {item.taxable ? (
                              <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700">Yes</span>
                            ) : (
                              <span className={`text-xs ${isDark ? 'text-slate-600' : 'text-slate-300'}`}>No</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* RECEIPTS TAB */}
      {activeTab === 'receipts' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardContent className="p-4">
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Total Sales</p>
                <p className={`text-xl font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>${stats.sales.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardContent className="p-4">
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Transactions</p>
                <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>{stats.transactions}</p>
              </CardContent>
            </Card>
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardContent className="p-4">
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Avg Receipt</p>
                <p className={`text-xl font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>${(stats.sales / stats.transactions).toFixed(2)}</p>
              </CardContent>
            </Card>
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardContent className="p-4">
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Items Sold</p>
                <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>{stats.items}</p>
              </CardContent>
            </Card>
          </div>

          <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-lg ${isDark ? 'text-white' : 'text-slate-800'}`}>Today's Receipts</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={isDark ? 'border-b border-slate-700 bg-slate-700/30' : 'border-b border-slate-200 bg-slate-50'}>
                      <th className={`text-left p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Receipt #</th>
                      <th className={`text-left p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Time</th>
                      <th className={`text-center p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Register</th>
                      <th className={`text-center p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Items</th>
                      <th className={`text-right p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Subtotal</th>
                      <th className={`text-right p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Tax</th>
                      <th className={`text-right p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Total</th>
                      <th className={`text-center p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Payment</th>
                      <th className={`text-center p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receipts.map((receipt, idx) => (
                      <tr key={idx} className={`${isDark ? 'border-b border-slate-700/50 hover:bg-slate-700/30' : 'border-b border-slate-100 hover:bg-slate-50'} transition-colors`}>
                        <td className={`p-3 font-mono text-sm ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{receipt.id}</td>
                        <td className={`p-3 ${isDark ? 'text-white' : 'text-slate-800'}`}>{receipt.time}</td>
                        <td className="p-3 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            receipt.register === 1 
                              ? isDark ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-100 text-blue-700'
                              : isDark ? 'bg-purple-900/50 text-purple-400' : 'bg-purple-100 text-purple-700'
                          }`}>
                            R{receipt.register}
                          </span>
                        </td>
                        <td className={`p-3 text-center ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{receipt.items}</td>
                        <td className={`p-3 text-right ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>${receipt.subtotal.toFixed(2)}</td>
                        <td className={`p-3 text-right ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>${receipt.tax.toFixed(2)}</td>
                        <td className={`p-3 text-right font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>${receipt.total.toFixed(2)}</td>
                        <td className="p-3 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            receipt.payment === 'Card' 
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-emerald-100 text-emerald-700'
                          }`}>
                            {receipt.payment}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <button className={`p-1.5 rounded ${isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}>
                            <Printer className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* SUPPLIER ORDERS TAB */}
      {activeTab === 'orders' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardContent className="p-4">
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Orders This Week</p>
                <p className={`text-xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>5</p>
              </CardContent>
            </Card>
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardContent className="p-4">
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Total Spent</p>
                <p className={`text-xl font-bold ${isDark ? 'text-red-400' : 'text-red-600'}`}>$3,310</p>
              </CardContent>
            </Card>
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardContent className="p-4">
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>In Transit</p>
                <p className={`text-xl font-bold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>1</p>
              </CardContent>
            </Card>
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardContent className="p-4">
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Suppliers</p>
                <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>5</p>
              </CardContent>
            </Card>
          </div>

          <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className={`text-lg ${isDark ? 'text-white' : 'text-slate-800'}`}>Purchase Orders</CardTitle>
                <button className={`px-4 py-2 rounded-lg font-medium text-sm ${isDark ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'}`}>
                  <Plus className="w-4 h-4 inline mr-1" /> New Order
                </button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={isDark ? 'border-b border-slate-700 bg-slate-700/30' : 'border-b border-slate-200 bg-slate-50'}>
                      <th className={`text-left p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>PO #</th>
                      <th className={`text-left p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Supplier</th>
                      <th className={`text-center p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Items</th>
                      <th className={`text-right p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Total</th>
                      <th className={`text-center p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Status</th>
                      <th className={`text-left p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Expected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {supplierOrders.map((order, idx) => (
                      <tr key={idx} className={`${isDark ? 'border-b border-slate-700/50 hover:bg-slate-700/30' : 'border-b border-slate-100 hover:bg-slate-50'} transition-colors`}>
                        <td className={`p-3 font-mono text-sm ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{order.id}</td>
                        <td className={`p-3 font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{order.supplier}</td>
                        <td className={`p-3 text-center ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{order.items}</td>
                        <td className={`p-3 text-right font-bold ${isDark ? 'text-red-400' : 'text-red-600'}`}>-${order.total.toFixed(2)}</td>
                        <td className="p-3 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            order.status === 'delivered' 
                              ? 'bg-emerald-100 text-emerald-700'
                              : order.status === 'in_transit'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {order.status === 'delivered' ? 'Delivered' : order.status === 'in_transit' ? 'In Transit' : 'Ordered'}
                          </span>
                        </td>
                        <td className={`p-3 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{order.expected}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* REPORTS TAB */}
      {activeTab === 'reports' && (
        <div className="space-y-4">
          {/* P&L Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className={isDark ? 'bg-emerald-900/30 border-emerald-700/50' : 'bg-emerald-50 border-emerald-200'}>
              <CardContent className="p-4">
                <p className={`text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>Total Revenue</p>
                <p className={`text-2xl font-bold ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>${stats.sales.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className={isDark ? 'bg-red-900/30 border-red-700/50' : 'bg-red-50 border-red-200'}>
              <CardContent className="p-4">
                <p className={`text-xs ${isDark ? 'text-red-400' : 'text-red-600'}`}>Total Costs</p>
                <p className={`text-2xl font-bold ${isDark ? 'text-red-300' : 'text-red-700'}`}>${stats.cost.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className={isDark ? 'bg-emerald-900/30 border-emerald-700/50' : 'bg-emerald-50 border-emerald-200'}>
              <CardContent className="p-4">
                <p className={`text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>Net Profit</p>
                <p className={`text-2xl font-bold ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>+${stats.profit.toLocaleString()}</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Revenue Breakdown */}
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardHeader className={`${isDark ? 'bg-emerald-900/20' : 'bg-emerald-50'} rounded-t-xl`}>
                <CardTitle className={`text-base flex items-center gap-2 ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
                  <ArrowUpRight className="w-5 h-5" />
                  Revenue by Category
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-slate-700/30">
                  {[
                    { cat: "Produce", amount: 845 },
                    { cat: "Homemade Foods", amount: 747 },
                    { cat: "Dairy", amount: 620 },
                    { cat: "Beverages", amount: 324 },
                    { cat: "Snacks", amount: 189 },
                    { cat: "Other", amount: 122 },
                  ].map((item, idx) => (
                    <div key={idx} className={`px-4 py-3 flex justify-between ${isDark ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50'}`}>
                      <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>{item.cat}</span>
                      <span className={`font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>${item.amount}</span>
                    </div>
                  ))}
                  <div className={`px-4 py-3 flex justify-between ${isDark ? 'bg-emerald-900/20' : 'bg-emerald-50'}`}>
                    <span className={`font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Total Revenue</span>
                    <span className={`font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>${stats.sales.toLocaleString()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Expenses Breakdown */}
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardHeader className={`${isDark ? 'bg-red-900/20' : 'bg-red-50'} rounded-t-xl`}>
                <CardTitle className={`text-base flex items-center gap-2 ${isDark ? 'text-red-400' : 'text-red-700'}`}>
                  <ArrowDownRight className="w-5 h-5" />
                  Expenses
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-slate-700/30">
                  {[
                    { exp: "Cost of Goods Sold", amount: stats.cost },
                    { exp: "Utilities", amount: selectedPeriod === 'today' ? 45 : selectedPeriod === 'week' ? 315 : 1350 },
                    { exp: "Staff Wages", amount: selectedPeriod === 'today' ? 180 : selectedPeriod === 'week' ? 1260 : 5400 },
                    { exp: "Supplies", amount: selectedPeriod === 'today' ? 25 : selectedPeriod === 'week' ? 175 : 750 },
                    { exp: "Rent", amount: selectedPeriod === 'month' ? 2800 : 0 },
                  ].filter(e => e.amount > 0).map((item, idx) => (
                    <div key={idx} className={`px-4 py-3 flex justify-between ${isDark ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50'}`}>
                      <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>{item.exp}</span>
                      <span className={`font-bold ${isDark ? 'text-red-400' : 'text-red-600'}`}>-${item.amount.toLocaleString()}</span>
                    </div>
                  ))}
                  <div className={`px-4 py-3 flex justify-between ${isDark ? 'bg-red-900/20' : 'bg-red-50'}`}>
                    <span className={`font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Total Expenses</span>
                    <span className={`font-bold ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                      -${(stats.cost + (selectedPeriod === 'today' ? 250 : selectedPeriod === 'week' ? 1750 : 10300)).toLocaleString()}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Net Profit Card */}
          <Card className={isDark ? 'bg-gradient-to-r from-emerald-900/40 to-slate-800 border-emerald-700/50' : 'bg-gradient-to-r from-emerald-50 to-white border-emerald-200'}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    Net Profit for {selectedPeriod === 'today' ? 'Today' : selectedPeriod === 'week' ? 'This Week' : 'This Month'}
                  </p>
                  <p className={`text-4xl font-bold mt-1 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                    +${stats.profit.toLocaleString()}
                  </p>
                </div>
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isDark ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}>
                  <TrendingUp className={`w-8 h-8 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                </div>
              </div>
              <div className={`mt-4 pt-4 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                <div className="flex items-center gap-6 text-sm">
                  <div>
                    <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Profit Margin: </span>
                    <span className="font-bold text-emerald-500">{((stats.profit / stats.sales) * 100).toFixed(1)}%</span>
                  </div>
                  <div>
                    <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Avg per Transaction: </span>
                    <span className="font-bold text-emerald-500">${(stats.profit / stats.transactions).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default GroceryStoreDemo;
